/**
 * Copyright (c) 2018-present, Ephox, Inc.
 *
 * This source code is licensed under the Apache 2 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { getTinymce } from '../TinyMCE'
import { isTextarea, mergePlugins, initEditor } from '../Utils'
import uuid from '@/helpers/uuid'
import { editorProps } from './EditorPropTypes'
import {
  h,
  defineComponent,
  onMounted,
  ref,
  Ref,
  toRefs,
  nextTick,
  watch,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
} from 'vue'
import { Editor as TinyMCEEditor, EditorEvent, TinyMCE } from 'tinymce'

type EditorOptions = Parameters<TinyMCE['init']>[0]

const renderInline = (
  ce: typeof h,
  id: string,
  elementRef: Ref<Element | null>,
  tagName?: string,
) =>
  ce(tagName ? tagName : 'div', {
    id,
    ref: elementRef,
  })

const renderIframe = (
  ce: typeof h,
  id: string,
  elementRef: Ref<Element | null>,
  props: typeof editorProps,
) =>
  !props.preview
    ? ce('textarea', {
        id,
        visibility: 'hidden',
        ref: elementRef,
        ['data-ua-unique-id']: 'unique-editor',
      })
    : ce(props.previewAsHTML ? 'div' : 'textarea', {
        id,
        ref: elementRef,
        style: props.previewAsHTML
          ? {
              display: 'block',
            }
          : {},
        ['data-ua-unique-id']: props.dataUaUniqueId || 'unique-editor',
      })

const DEFAULT_INIT = {
  font_formats:
    'Andale Mono=andale mono,times;' +
    'Arial=arial,helvetica,sans-serif;' +
    'Arial Black=arial black,avant garde;' +
    'Book Antiqua=book antiqua,palatino;' +
    'Comic Sans MS=comic sans ms,sans-serif;' +
    'Courier New=courier new,courier;' +
    'Georgia=georgia,palatino;' +
    'Helvetica=helvetica;' +
    'Impact=impact,chicago;' +
    'Tahoma=tahoma,arial,helvetica,sans-serif;' +
    'Terminal=terminal,monaco;' +
    'Times New Roman=times new roman,times;' +
    'Trebuchet MS=trebuchet ms,geneva;' +
    'Verdana=verdana,geneva;',
  menubar: 'tools table format view insert edit',
  height: 400,
  plugins: [
    'advlist',
    'autolink',
    'lists',
    'link',
    'image',
    'charmap',
    'preview',
    // 'hr',
    'anchor',
    'pagebreak',
    'searchreplace',
    'wordcount',
    'visualblocks',
    'visualchars',
    'code',
    'fullscreen',
    'insertdatetime',
    'media',
    'nonbreaking',
    'save',
    'table',
    // 'contextmenu',
    'directionality',
    'emoticons',
    // 'template',
    // 'paste',
    // 'textcolor',
  ],
  external_image_plugin: true,
  toolbar1:
    'styles fontfamily fontsize | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent',
  toolbar2: 'undo redo | preview image | forecolor backcolor | code fullscreen',
  image_advtab: true,
  invalid_elements: 'iframe, script, object, style',
  entity_encoding: 'raw',
}

export const Editor = defineComponent({
  name: 'lp-editor',
  props: editorProps,
  setup: (props, ctx) => {
    let conf = props.init ? { ...props.init } : { ...DEFAULT_INIT }
    const { disabled, modelValue, tagName, preview } = toRefs(props)
    const element: Ref<Element | null> = ref(null)
    let vueEditor: TinyMCEEditor | null = null
    const elementId: string = props.id || uuid('tiny-vue')
    const inlineEditor: boolean =
      (props.init && props.init.inline) || props.inline
    const modelBind = !!ctx.attrs['onUpdate:modelValue']
    let mounting = true
    const initialValue: string = props.initialValue ? props.initialValue : ''
    let cache = ''

    const getContent = (isMounting: boolean): (() => string) =>
      modelBind
        ? () => (modelValue?.value ? modelValue.value : '')
        : () => (isMounting ? initialValue : cache)

    const initWrapper = async (): Promise<void> => {
      if (props.preview) {
        if (props.previewAsHTML) {
          ;(element.value as HTMLElement).innerHTML = props.modelValue
        } else {
          ;(element.value as HTMLTextAreaElement).value = props.modelValue
        }
        return
      }

      const content = getContent(mounting)
      const defaultContent = await import(
        'node_modules/tinymce/skins/content/default/content.min.css'
      )
      const skinStyles = await import(
        'node_modules/tinymce/skins/ui/oxide/content.inline.min.css'
      )
      const finalInit = {
        ...conf,
        readonly: props.disabled,
        selector: `#${elementId}`,
        plugins: mergePlugins(conf.plugins, props.plugins),
        toolbar: props.toolbar || conf.toolbar,
        inline: inlineEditor,
        // base_url: '/node_modules/tinymce/',
        content_style: `${defaultContent.default} ${skinStyles.default}`,
        content_css: false,
        // skin_url: skinStyles.default.replace('/content.inline.min.css', ''),
        promotion: false,
        branding: false,
        setup: (editor: TinyMCEEditor) => {
          vueEditor = editor
          editor.on('init', (e: EditorEvent<unknown>) =>
            initEditor(e, props, ctx, editor, modelValue, content),
          )
          if (typeof conf.setup === 'function') {
            conf.setup(editor)
          }
        },
      }
      if (isTextarea(element.value)) {
        element.value.style.visibility = ''
      }
      getTinymce().init(finalInit)
      mounting = false
    }

    watch(disabled, (disable) => {
      if (vueEditor !== null) {
        if (typeof vueEditor.mode?.set === 'function') {
          vueEditor.mode.set(disable ? 'readonly' : 'design')
        } else if (
          'setMode' in vueEditor &&
          typeof vueEditor.setMode === 'function'
        ) {
          vueEditor.setMode(disable ? 'readonly' : 'design')
        }
      }
    })

    watch(tagName, () => {
      if (!modelBind && vueEditor) {
        cache = vueEditor.getContent()
      }
      getTinymce()?.remove(vueEditor)
      nextTick(() => initWrapper())
    })

    async function onMountedHook() {
      await import('tinymce')
      await import('tinymce/icons/default/icons.min.js')
      await import('tinymce/themes/silver/theme.min.js')
      await import('tinymce/skins/ui/oxide/skin.js')
      await import('tinymce/models/dom/model.min.js')
      await import('tinymce/plugins/advlist')
      await import('tinymce/plugins/autolink')
      await import('tinymce/plugins/lists')
      await import('tinymce/plugins/link')
      await import('tinymce/plugins/image')
      await import('tinymce/plugins/code')
      await import('tinymce/plugins/charmap')
      await import('tinymce/plugins/preview')
      await import('tinymce/plugins/anchor')
      await import('tinymce/plugins/pagebreak')
      await import('tinymce/plugins/searchreplace')
      await import('tinymce/plugins/wordcount')
      await import('tinymce/plugins/visualchars')
      await import('tinymce/plugins/fullscreen')
      await import('tinymce/plugins/insertdatetime')
      await import('tinymce/plugins/media')
      await import('tinymce/plugins/nonbreaking')
      await import('tinymce/plugins/save')
      await import('tinymce/plugins/table')
      await import('tinymce/plugins/directionality')
      await import('tinymce/plugins/emoticons')
      await import('tinymce/plugins/emoticons/js/emojis.min.js')
      await import('tinymce/plugins/visualblocks')
      await initWrapper()
    }
    onMounted(onMountedHook)

    watch(preview, async () => {
      if (getTinymce() !== null) {
        getTinymce().remove(vueEditor)
      }
      onMountedHook()
    })

    onBeforeUnmount(() => {
      if (getTinymce() !== null) {
        getTinymce().remove(vueEditor)
      }
    })

    if (!inlineEditor) {
      onActivated(() => {
        if (!mounting) {
          initWrapper()
        }
      })

      onDeactivated(() => {
        if (!modelBind && vueEditor) {
          cache = vueEditor.getContent()
        }
        getTinymce()?.remove(vueEditor)
      })
    }

    const rerender = (init: EditorOptions) => {
      if (vueEditor) {
        cache = vueEditor.getContent()
        getTinymce()?.remove(vueEditor)
        conf = { ...conf, ...init }
        nextTick(() => initWrapper())
      }
    }

    ctx.expose({
      rerender,
    })

    return () =>
      inlineEditor
        ? renderInline(h, elementId, element, props.tagName)
        : [
            props.label
              ? h(
                  'p',
                  {
                    class: 'lp-input-label lead',
                    innerHTML: `${props.label}${
                      props.required ? '<span class="text-blue">*</span>' : ''
                    }`,
                  },
                  [],
                )
              : null,
            renderIframe(h, elementId, element, props),
            props.message
              ? h('p', {
                  class: [
                    'lp-input-message mt-half d-flex',
                    props.status
                      ? `lp-input-message-${props.status}`
                      : `lp-input-message-danger`,
                    props.disabled && `lp-input-message-disabled`,
                  ],
                  innerText: props.message,
                })
              : null,
          ]
  },
})
